/**
 * Structure for the decorative trabeated colonnade: which parts exist, in what order, and how
 * they move.
 *
 * The structure is post-and-lintel architecture — the thing the company is named after — drawn
 * entirely with `<div>`s, borders and CSS gradients. No image, no SVG, no canvas.
 *
 * It lives here as a table rather than as markup because the prototype writes six near-identical
 * columns of six parts each: thirty-six elements differing only in which one carries the accent.
 * Transcribed into JSX that becomes the largest block of duplication in the page, and the accent
 * column disappears into a wall of identical elements. Driven from this table, moving the accent
 * is a one-value edit.
 *
 * ── The geometry is not here ────────────────────────────────────────────────────────────────
 * Every width and height lives in `Colonnade.module.css`, in each part's `[data-part]` rule, and
 * nowhere else. They used to live here and reach the page as inline `style` attributes — 48 of
 * them — which forced the policy to allow inline style. The served page now carries none, so the
 * policy is `style-src 'self'` with no exception (REQ-LANDING-008, change 0004 D3). One copy of
 * each number, in the stylesheet that uses it, rather than a copy here plus a drift test.
 *
 * Adding a column is `COLUMN_COUNT` here plus one `[data-column]` rule there;
 * `tests/Colonnade.test.tsx` fails until both agree.
 */

/** Which rest-offset and assembly delay a piece takes. Built base-up: the base moves first. */
export type PieceGroup = 'cornice' | 'architrave' | 'column' | 'stylobate';

export interface CoursePiece {
  /** Drives `data-part`, which the stylesheet uses to apply that piece's size and carving. */
  readonly id: string;
  readonly group: PieceGroup;
}

export interface ColumnPiece {
  readonly id: string;
}

/** The entablature, above the columns. Rendered top to bottom, and moves as one piece. */
export const CORNICE_PIECES: readonly CoursePiece[] = [
  { id: 'cornice-slab', group: 'cornice' },
  { id: 'dentils', group: 'cornice' },
];

/** The beam the columns carry — the lintel of "post and lintel". */
export const ARCHITRAVE: CoursePiece = { id: 'architrave', group: 'architrave' };

/** The stepped platform the columns stand on. Rendered top to bottom. */
export const STYLOBATE_PIECES: readonly CoursePiece[] = [
  { id: 'stylobate-1', group: 'stylobate' },
  { id: 'stylobate-2', group: 'stylobate' },
];

/** One column, top to bottom. The shaft takes the remaining height. */
export const COLUMN_PIECES: readonly ColumnPiece[] = [
  { id: 'abacus' },
  { id: 'echinus' },
  { id: 'necking' },
  { id: 'shaft' },
  { id: 'torus' },
  { id: 'plinth' },
];

export const COLUMN_COUNT = 6;

/** Zero-based. The one column drawn in ink rather than line — the single accent in the
 *  structure. Moving it is this one number. */
export const ACCENT_COLUMN_INDEX = 2;
